import { useCallback, useEffect, useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError } from './client.js';

/**
 * The texts a parent agrees to when adding a child (GET /api/waivers).
 *
 * Templates, with {guardian} and {athlete} still in them, so the names can be
 * filled as they are typed. `reload` is for the rare refusal that says the text
 * changed while the form was open.
 */

export interface Waiver {
  waiver_id: string;
  version: number;
  title: string;
  template: string;
}

export interface Waivers {
  participation: Waiver | null;
  media: Waiver | null;
  /** An adult for themselves (036). */
  adult_participation: Waiver | null;
  adult_media: Waiver | null;
}

export type WaiversState =
  | { status: 'loading' }
  | { status: 'ready'; waivers: Waivers }
  | { status: 'failed'; message: string };

export function useWaivers(): { state: WaiversState; reload: () => void } {
  const getToken = useAccessToken();
  const [state, setState] = useState<WaiversState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    apiFetch<Waivers>('/api/waivers', getToken)
      .then((waivers) => {
        if (!cancelled) setState({ status: 'ready', waivers });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setState({
          status: 'failed',
          message: cause instanceof ApiError ? cause.message : 'The waiver could not be read. Try again.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, nonce]);

  return { state, reload };
}
